import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Plus, Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import RichEditor from "@/components/RichEditor";
import { findTokens, renderPreviewHtml, renderPreviewSubject, previewLayoutStyles } from "@/lib/tokens";
import { sanitizeBodyHtml } from "@/lib/sanitize";
import { BrandMark } from "@/components/BrandMark";

const BUILTIN_TOKENS = [
    "Client name",
    "Lender",
    "Property address",
    "Loan amount",
    "Rate",
    "Monthly payment",
    "Appointment date",
];

export default function TemplateEditorPage() {
    const { id } = useParams();
    const isNew = !id;
    const nav = useNavigate();
    const { user } = useAuth();
    const { brands, categories, brandByKey, customFields, addCustomField, reloadTemplates, emailStyles, styleById, resolveStyleForTemplate } = useData();

    const [name, setName] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [brandKey, setBrandKey] = useState("");
    const [category, setCategory] = useState("");
    const [shared, setShared] = useState(true);
    const [draft, setDraft] = useState(false);
    const [styleId, setStyleId] = useState(""); // "" = use brand default
    const [description, setDescription] = useState("");
    const [usageScenario, setUsageScenario] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [mode, setMode] = useState("rich"); // "rich" | "html"
    const [newFieldName, setNewFieldName] = useState("");
    const subjectRef = useRef(null);
    const [subjectCaret, setSubjectCaret] = useState(0);

    // Set initial brand + category once metadata loads (for new templates)
    useEffect(() => {
        if (isNew) {
            if (!brandKey && brands.length) setBrandKey(brands[0].key);
            if (!category && categories.length) setCategory(categories[0].name);
        }
    }, [isNew, brands, categories, brandKey, category]);

    // Load existing template
    useEffect(() => {
        if (!id) return;
        api.get(`/templates/${id}`).then(
            (r) => {
                const t = r.data;
                if (user.access_level !== "admin" && t.owner_id !== user.id) {
                    toast.error("You cannot edit this template", { position: "bottom-center" });
                    nav(`/templates/${id}`);
                    return;
                }
                setName(t.name);
                setSubject(t.subject);
                setBody(t.body);
                setBrandKey(t.brand_key);
                setCategory(t.category);
                setShared(!!t.shared);
                setDraft(!!t.draft);
                setStyleId(t.style_id || "");
                setDescription(t.description || "");
                setUsageScenario(t.usage_scenario || "");
            },
            (e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
        );
    }, [id, nav, user]);

    const tokens = useMemo(() => findTokens(subject, body), [subject, body]);

    const allChips = useMemo(() => {
        const set = new Set([...BUILTIN_TOKENS, ...customFields]);
        return Array.from(set);
    }, [customFields]);

    const insertTokenIntoSubject = (tok) => {
        const el = subjectRef.current;
        if (!el) {
            setSubject((s) => s + `{${tok}}`);
            return;
        }
        const start = el.selectionStart ?? subject.length;
        const end = el.selectionEnd ?? subject.length;
        const before = subject.slice(0, start);
        const after = subject.slice(end);
        const insertion = `{${tok}}`;
        const next = before + insertion + after;
        setSubject(next);
        requestAnimationFrame(() => {
            el.focus();
            const pos = start + insertion.length;
            el.setSelectionRange(pos, pos);
        });
    };

    // We need to detect where the last focus was — subject vs body — to route
    // "Insert field" chip clicks. Track via focus events.
    const [lastFocus, setLastFocus] = useState("body");

    const insertToken = (tok) => {
        if (lastFocus === "subject") {
            insertTokenIntoSubject(tok);
        } else {
            // Insert into contenteditable body via execCommand — RichEditor handles rich mode via focus.
            const insertion = `{${tok}}`;
            if (mode === "rich") {
                // Try to insert at caret in contenteditable via document.execCommand
                const el = document.querySelector('[data-testid="editor-body-contenteditable"]');
                if (el) {
                    el.focus();
                    document.execCommand("insertText", false, insertion);
                    setBody(el.innerHTML);
                    return;
                }
            }
            // HTML mode / fallback: append at end
            setBody((b) => b + insertion);
        }
    };

    const submitCustomField = () => {
        const n = newFieldName.trim();
        if (!n) return;
        addCustomField(n);
        setNewFieldName("");
        toast.success(`Added field "${n}"`, { position: "bottom-center" });
    };

    const canSave = name.trim() && subject.trim() && body.trim() && brandKey && category;

    const onSave = async () => {
        if (!canSave) {
            toast.error("Please fill in name, subject, body, brand and category", { position: "bottom-center" });
            return;
        }
        setBusy(true);
        try {
            const payload = { name, subject, body, brand_key: brandKey, category, shared, draft, style_id: styleId || null, description, usage_scenario: usageScenario };
            let resp;
            if (isNew) {
                resp = await api.post("/templates", payload);
                toast.success("Template created", { position: "bottom-center" });
            } else {
                resp = await api.put(`/templates/${id}`, payload);
                toast.success("Template saved", { position: "bottom-center" });
            }
            await reloadTemplates();
            nav(`/templates/${resp.data.id}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const brand = brandByKey(brandKey);
    const editorEffectiveStyle = resolveStyleForTemplate({ brand_key: brandKey, style_id: styleId || null });
    const editorPreviewStyles = previewLayoutStyles(editorEffectiveStyle);
    // Compute the "Brand default (…)" label defensively. On a partially-
    // hydrated DB the resolved style may not match the brand's stored
    // default_style_id — always fall back to a plain "Brand default".
    const brandDefaultStyle = brand?.default_style_id ? styleById(brand.default_style_id) : null;
    const brandDefaultLabel = brandDefaultStyle?.name
        ? `Brand default (${brandDefaultStyle.name})`
        : "Brand default";
    const previewBodyHtml = sanitizeBodyHtml(renderPreviewHtml(body, {}));
    const previewSubject = sanitizeBodyHtml(renderPreviewSubject(subject, {}));

    if (error) {
        return (
            <div className="max-w-3xl mx-auto p-8">
                <button className="btn-ghost mb-6" onClick={() => nav(-1)}><ArrowLeft size={14}/> Back</button>
                <div className="tms-card p-8" style={{ color: "var(--tms-danger)" }}>{error}</div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-4" data-testid="template-editor-page">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6 fade-in">
                <button className="btn-ghost" onClick={() => nav(-1)} data-testid="editor-cancel-btn">
                    <X size={14} /> Cancel
                </button>
                <h1 style={{ fontSize: 20, fontWeight: 600, color: "var(--tms-navy)" }} data-testid="editor-title">
                    {isNew ? "New template" : "Edit template"}
                </h1>
                <button className="btn-primary-navy" onClick={onSave} disabled={busy || !canSave} data-testid="editor-save-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                    {busy ? "Saving…" : "Save"}
                </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* FORM */}
                <div className="space-y-5">
                    <div className="tms-card p-5 space-y-4">
                        <div>
                            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                Template name
                            </label>
                            <input
                                data-testid="editor-name-input"
                                className="tms-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Mortgage offer approved"
                            />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                    Category
                                </label>
                                <select
                                    data-testid="editor-category-select"
                                    className="tms-select w-full"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    disabled={categories.length === 0}
                                >
                                    <option value="">
                                        {categories.length === 0 ? "No categories yet" : "Choose a category…"}
                                    </option>
                                    {categories.map((c) => (
                                        <option key={c.name} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                                {categories.length === 0 && (
                                    <p
                                        data-testid="editor-no-categories-hint"
                                        style={{ fontSize: 11, color: "var(--tms-danger)", marginTop: 6, lineHeight: 1.5 }}
                                    >
                                        Create a category in <a href="/settings" style={{ textDecoration: "underline", color: "var(--tms-navy)", fontWeight: 500 }}>Settings</a> first, then come back to save this template.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                    Brand
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {brands.map((b) => (
                                        <button
                                            key={b.key}
                                            type="button"
                                            className="tms-chip"
                                            onClick={() => setBrandKey(b.key)}
                                            style={{
                                                background: brandKey === b.key ? "var(--tms-navy)" : "#eef0f3",
                                                color: brandKey === b.key ? "#fff" : "var(--tms-navy)",
                                                height: 32,
                                                padding: "0 14px",
                                                cursor: "pointer",
                                                gap: 8,
                                            }}
                                            data-testid={`editor-brand-${b.key}`}
                                        >
                                            <BrandMark brand={b} code={b.short_code} size="sm" /> {b.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                Email style
                            </label>
                            <select
                                className="tms-select"
                                value={styleId}
                                onChange={(e) => setStyleId(e.target.value)}
                                data-testid="editor-style-select"
                                style={{ maxWidth: 360 }}
                            >
                                <option value="">{brandDefaultLabel}</option>
                                {emailStyles.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                            <p style={{ fontSize: 11, color: "var(--tms-muted)", marginTop: 4 }}>
                                Formatting only — pick from styles configured by an admin. Doesn&apos;t change the body text.
                            </p>
                        </div>

                        <div>
                            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                Subject line
                            </label>
                            <input
                                data-testid="editor-subject-input"
                                ref={subjectRef}
                                className="tms-input"
                                value={subject}
                                onFocus={() => setLastFocus("subject")}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="e.g. Great news, {Client name} — your offer is here"
                            />
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between mb-1.5">
                                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)" }}>
                                    Short description <span style={{ fontWeight: 400, color: "var(--tms-faint)" }}>(optional)</span>
                                </label>
                                <span
                                    data-testid="editor-description-counter"
                                    style={{
                                        fontSize: 11,
                                        color: description.length > 200 ? "var(--tms-danger)" : "var(--tms-faint)",
                                    }}
                                >
                                    {description.length}/200
                                </span>
                            </div>
                            <textarea
                                data-testid="editor-description-input"
                                className="tms-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                                placeholder="One-line summary shown on the card — what this template is for."
                                rows={2}
                                style={{ resize: "vertical", minHeight: 52, padding: "8px 12px", lineHeight: 1.5 }}
                            />
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between mb-1.5">
                                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)" }}>
                                    When to use it <span style={{ fontWeight: 400, color: "var(--tms-faint)" }}>(optional)</span>
                                </label>
                                <span
                                    data-testid="editor-usage-counter"
                                    style={{
                                        fontSize: 11,
                                        color: usageScenario.length > 400 ? "var(--tms-danger)" : "var(--tms-faint)",
                                    }}
                                >
                                    {usageScenario.length}/400
                                </span>
                            </div>
                            <textarea
                                data-testid="editor-usage-input"
                                className="tms-input"
                                value={usageScenario}
                                onChange={(e) => setUsageScenario(e.target.value.slice(0, 400))}
                                placeholder="Which client situation, milestone or trigger this template fits."
                                rows={3}
                                style={{ resize: "vertical", minHeight: 68, padding: "8px 12px", lineHeight: 1.5 }}
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                                Email body
                            </label>
                            <div onFocus={() => setLastFocus("body")}>
                                <RichEditor value={body} onChange={setBody} mode={mode} setMode={setMode} />
                            </div>
                        </div>
                    </div>

                    {/* Insert field row */}
                    <div className="tms-card p-5">
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 8 }}>
                            Insert field
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {allChips.map((tok) => (
                                <button
                                    key={tok}
                                    type="button"
                                    className="tms-chip"
                                    onClick={() => insertToken(tok)}
                                    style={{
                                        background: "var(--tms-green-tint)",
                                        color: "#4d7b1b",
                                        border: "1px solid #d3e8a6",
                                        cursor: "pointer",
                                        height: 26,
                                        padding: "0 10px",
                                    }}
                                    data-testid={`insert-field-${tok.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    {`{${tok}}`}
                                </button>
                            ))}
                            <div className="flex items-center gap-1">
                                <input
                                    className="tms-input"
                                    placeholder="New field name"
                                    value={newFieldName}
                                    onChange={(e) => setNewFieldName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submitCustomField())}
                                    style={{ height: 30, padding: "0 12px", fontSize: 12, width: 140 }}
                                    data-testid="new-field-input"
                                />
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    onClick={submitCustomField}
                                    style={{ padding: "4px 8px" }}
                                    data-testid="new-field-submit-btn"
                                >
                                    <Plus size={12} /> Field
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sharing + draft */}
                    <div className="tms-card p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)" }}>Share with the whole team</div>
                                <div style={{ fontSize: 12, color: "var(--tms-muted)" }}>
                                    Everyone at The Mortgage Stop will see this template.
                                </div>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={shared}
                                data-testid="editor-shared-toggle"
                                onClick={() => setShared((s) => !s)}
                                className="relative rounded-full transition-colors"
                                style={{
                                    width: 44, height: 24,
                                    background: shared ? "var(--tms-green)" : "#c9d0d9",
                                }}
                            >
                                <span
                                    className="absolute rounded-full bg-white transition-transform"
                                    style={{
                                        width: 18, height: 18, top: 3, left: 3,
                                        transform: shared ? "translateX(20px)" : "translateX(0)",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                    }}
                                />
                            </button>
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                            <input
                                id="draft-toggle"
                                type="checkbox"
                                checked={draft}
                                onChange={(e) => setDraft(e.target.checked)}
                                data-testid="editor-draft-toggle"
                            />
                            <label htmlFor="draft-toggle" style={{ fontSize: 13, color: "var(--tms-navy)" }}>
                                Save as draft (only you will see this)
                            </label>
                        </div>
                    </div>

                    {/* Tokens box */}
                    <div className="tms-card p-5">
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 6 }}>
                            Fill-in fields detected
                        </div>
                        {tokens.length === 0 ? (
                            <div style={{ fontSize: 12, color: "var(--tms-muted)" }}>
                                No merge fields detected yet. Insert one from the chips above.
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2" data-testid="detected-tokens-list">
                                {tokens.map((t) => (
                                    <span key={t} className="tms-chip" style={{ background: "var(--tms-green-tint)", color: "#4d7b1b", border: "1px solid #d3e8a6" }}>
                                        {`{${t}}`}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* PREVIEW */}
                <div>
                    <div className="tms-card p-5 sticky top-[80px]" style={{ maxHeight: "calc(100vh - 100px)", overflow: "auto" }}>
                        <div style={{ fontSize: 12, color: "var(--tms-muted)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>
                            Live preview
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                            <BrandMark brand={brand} code={brand?.short_code || "?"} size="md" />
                            <span style={{ fontSize: 12, color: "var(--tms-muted)" }}>{brand?.label}</span>
                        </div>
                        <div className="pb-3 mb-3 border-b" style={{ borderColor: "var(--tms-border)" }}>
                            <div style={{ fontSize: 11, color: "var(--tms-muted)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                                Subject
                            </div>
                            <div style={{ fontSize: 14, color: "var(--tms-navy)", fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: previewSubject || "<em style=\"color:#9AA4AE\">(no subject yet)</em>" }} />
                        </div>
                        <div
                            data-testid="editor-preview-body"
                            style={editorPreviewStyles.outer}
                        >
                            <div
                                style={{ ...editorPreviewStyles.inner, fontSize: 14 }}
                                dangerouslySetInnerHTML={{ __html: previewBodyHtml || "<em style=\"color:#9AA4AE\">Start typing to see your email…</em>" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
